import type { Pathname } from '../../navigation/routes.gen';
import { APP_MODULES, type AppModule } from '../system/app';

/**
 * The complete set of dashboard tabs, business-owned here because the Lite tier's
 * locked/usable split below is defined over it. Each section maps 1:1 to a literal
 * child route of `/console/$orgId/dashboard` (see `LITE_LOCKED_DASHBOARD_ROUTES`).
 */
export const DASHBOARD_SECTIONS = [
    'management',
    'complaints',
    'incidents',
    'people-and-culture',
    'risks',
    'claims',
] as const;

export type DashboardSection = (typeof DASHBOARD_SECTIONS)[number];

/**
 * Curium Lite — the trimmed trial tier (CUR-379).
 *
 * A Lite org (`Org.isLiteOrg`) gets a curated subset of the product; everything else is
 * visible-but-locked behind an upgrade prompt. This file is the single source of truth for
 * what Lite includes, shared by server enforcement (effective modules, `liteBlocked` meta)
 * and client presentation (locked nav items, route guard, configuration menu).
 */

/**
 * Modules a Lite org can effectively use. The server intersects `Org.enabledModules` with this
 * list (see `getEffectiveEnabledModules`), so an admin misconfiguration can never widen a Lite
 * org's access.
 */
export const LITE_ALLOWED_MODULES = [APP_MODULES.COMPLIANCE] as const satisfies readonly AppModule[];

/**
 * The module set enforcement actually operates on. Identity for regular orgs; the intersection
 * with `LITE_ALLOWED_MODULES` for Lite orgs. Applied at the claims chokepoint
 * (`getAuth0UserClaims`) so web tRPC, login bootstrap and the MCP bridge all agree, and mirrored
 * in `org.getOrg` so the client never sees a wider module set than the server enforces.
 */
export function getEffectiveEnabledModules(enabledModules: readonly AppModule[], isLiteOrg: boolean): AppModule[] {
    if (!isLiteOrg) {
        return [...enabledModules];
    }
    return enabledModules.filter((module) => (LITE_ALLOWED_MODULES as readonly AppModule[]).includes(module));
}

/**
 * The Lite status a *session* is treated with (CUR-379). `Org.isLiteOrg` is the raw org tier — a
 * customer upgrade wall — but a Curium **support account** (vendor staff administering a trial org
 * through an approved, audited support session, acting as themselves with delegated roles) is not
 * the customer that wall exists for. So for a support account the session behaves as a normal org:
 * no upgrade locks, no obligation redaction, full compliance surface.
 *
 * Computed once at the two session sources — `getAuth0UserClaims` (server enforcement + MCP) and
 * `getOrg` (client bootstrap) — so every downstream `isLiteOrg` reader (the `liteBlocked` gate,
 * obligation redaction, client nav crown, route guard, complaint/incident AI-analysis gates) treats
 * staff as non-Lite with no per-call carve-out. The exemption lives here and nowhere else.
 *
 * The raw `Org.isLiteOrg` is deliberately left untouched: it still caps the module set via
 * `getEffectiveEnabledModules` (so a support account stays confined to the org's actual,
 * compliance-only module set) and still drives org-level analytics/billing.
 */
export function getEffectiveIsLiteOrg({
    isLiteOrg,
    isSupportAccount,
}: {
    isLiteOrg: boolean;
    isSupportAccount: boolean;
}): boolean {
    return isLiteOrg && !isSupportAccount;
}

/** Where every upgrade CTA points (opened in a new tab). */
export const LITE_UPGRADE_URL = 'https://www.getcurium.com/contact';

/**
 * Machine-matchable prefix on FORBIDDEN errors thrown by the `liteBlocked` gate, so the client
 * can map any missed lock to a friendly "requires an upgrade" toast instead of a raw 403.
 */
export const LITE_UPGRADE_ERROR_PREFIX = 'upgrade_required';

/**
 * Product areas locked for Lite orgs. Nav shows them crowned (upgrade pill); direct URL entry
 * renders the locked page. Paths are route-definition paths (params uninterpolated) and match
 * on segment boundaries via `isLiteLockedRoutePath`.
 */
export const LITE_LOCKED_ROUTE_PREFIXES = [
    '/console/$orgId/claims',
    '/console/$orgId/reporting',
    '/console/$orgId/risk',
    '/console/$orgId/controls',
    '/console/$orgId/mspr',
    '/console/$orgId/attestations',
    '/console/$orgId/documents',
    '/console/$orgId/tools',
    '/console/$orgId/compliance/obligations',
    '/console/$orgId/workload-manager/emails',
] as const satisfies readonly Pathname[];

/**
 * Configuration sections hidden (not crowned) for Lite orgs — dropped from the configuration
 * menu and landing grid, and blocked by the route guard on direct URL entry.
 */
export const LITE_HIDDEN_CONFIG_ROUTE_PREFIXES = [
    '/console/$orgId/configuration/org-settings/system',
    '/console/$orgId/configuration/org-settings/communication',
    '/console/$orgId/configuration/org-settings/tasks',
    '/console/$orgId/configuration/org-settings/trash',
    '/console/$orgId/configuration/claims-products',
    '/console/$orgId/configuration/financials',
    // Compliance settings are template-locked for Lite, so showing them is pure noise — hide
    // every section except Training (the one Lite orgs actively manage).
    '/console/$orgId/configuration/compliance/complaints',
    '/console/$orgId/configuration/compliance/incidents',
    '/console/$orgId/configuration/compliance/general',
    '/console/$orgId/configuration/compliance/obligations',
    '/console/$orgId/configuration/compliance/attestations',
    '/console/$orgId/configuration/compliance/mspr',
    '/console/$orgId/configuration/compliance/auto-detect',
    '/console/$orgId/configuration/risk',
    '/console/$orgId/configuration/vendor-management',
    '/console/$orgId/configuration/monitoring',
    '/console/$orgId/configuration/api-integrations',
] as const satisfies readonly Pathname[];

/**
 * Configuration routes that stay fully usable for Lite orgs (team management, handling
 * parties and support-session approval, plus the section shells that host them).
 */
export const LITE_CONFIG_USABLE_ROUTES = [
    '/console/$orgId/configuration',
    '/console/$orgId/configuration/org-settings',
    '/console/$orgId/configuration/org-settings/users',
    '/console/$orgId/configuration/org-settings/handling-parties',
    '/console/$orgId/configuration/org-settings/support-sessions',
] as const satisfies readonly Pathname[];

/**
 * Configuration routes that stay visible for Lite orgs but are template-locked: pages render
 * read-only with a lock banner, and the backing mutations carry `liteBlocked` meta server-side.
 * Exception: within the training page, training *periods* are user-managed on Lite (CUR-417) —
 * only the STAFF_TRAINING_RECORD template stays locked.
 */
export const LITE_CONFIG_LOCKED_VISIBLE_ROUTES = [
    '/console/$orgId/configuration/compliance',
    '/console/$orgId/configuration/compliance/training',
] as const satisfies readonly Pathname[];

/** Dashboard tabs locked (crowned) for Lite orgs; the rest stay usable. */
export const LITE_LOCKED_DASHBOARD_SECTIONS = [
    'management',
    // People & Culture surfaces controls content Lite orgs don't hold, so it's a premium tab (CUR-418).
    'people-and-culture',
    'risks',
    'claims',
] as const satisfies readonly DashboardSection[];

/** True when a dashboard section is locked (crowned) on the Lite tier. */
export function isLiteLockedDashboardSection(section: DashboardSection): boolean {
    return (LITE_LOCKED_DASHBOARD_SECTIONS as readonly DashboardSection[]).includes(section);
}

/**
 * Route paths of the Lite-locked dashboard tabs — the literal child routes of
 * `/console/$orgId/dashboard` matching `LITE_LOCKED_DASHBOARD_SECTIONS` (a test keeps the
 * two in sync). Used by the client `LiteRouteGuard` to block direct URLs.
 */
export const LITE_LOCKED_DASHBOARD_ROUTES = [
    '/console/$orgId/dashboard/management',
    '/console/$orgId/dashboard/people-and-culture',
    '/console/$orgId/dashboard/risks',
    '/console/$orgId/dashboard/claims',
] as const satisfies readonly Pathname[];

/** True when the route path is a Lite-locked dashboard tab. */
export function isLiteLockedDashboardRoutePath(routePath: string): boolean {
    return (LITE_LOCKED_DASHBOARD_ROUTES as readonly string[]).includes(routePath);
}

function matchesRoutePrefix(routePath: string, prefix: string): boolean {
    return routePath === prefix || routePath.startsWith(`${prefix}/`);
}

/**
 * True when the path is a Lite-locked product area — shown in the nav with an upgrade pill,
 * click opens the contact page. Matches on segment boundaries so `/claims` never captures an
 * unrelated `/claims-foo` sibling. Paths are route-definition paths (params uninterpolated).
 */
export function isLiteUpgradeLockedRoutePath(routePath: string): boolean {
    return LITE_LOCKED_ROUTE_PREFIXES.some((prefix) => matchesRoutePrefix(routePath, prefix));
}

/** True when the path is a configuration section hidden entirely for Lite orgs (no upgrade pill). */
export function isLiteHiddenRoutePath(routePath: string): boolean {
    return LITE_HIDDEN_CONFIG_ROUTE_PREFIXES.some((prefix) => matchesRoutePrefix(routePath, prefix));
}

/**
 * True when a route-definition path falls inside any Lite-blocked area (locked product areas and
 * hidden configuration sections alike) — the direct-URL route guard uses this union.
 */
export function isLiteLockedRoutePath(routePath: string): boolean {
    return isLiteUpgradeLockedRoutePath(routePath) || isLiteHiddenRoutePath(routePath);
}
